import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { AirplaneTakeoff, AirplaneLanding, Path } from "@phosphor-icons/react";
import AddRouteDialog from "../../components/admin/AddRouteDialog";
import AdminNav from "../../components/AdminNav";
import { AirportPickupCell, RoutePickupCell, maskToDays } from "./PickupCalendarAdmin";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";

/** Per-airport Default Concierge Accounts selector (ACTIVE ACCOUNTS mapped to that airport).
 *  CONTROLLED: staged only — selecting a value never persists on its own. */
function ConciergeDefaultCell({ airport, value, onChange }) {
  const [eligible, setEligible] = useState(null);
  const current = value || "";
  const loadEligible = async () => {
    if (eligible !== null) return;
    try {
      const { data } = await api.get(`/admin/airports/${airport.code}/concierge-eligible-accounts`);
      setEligible(data.accounts || []);
    } catch (e) { setEligible([]); }
  };
  return (
    <select
      data-testid={`concierge-default-${airport.code}`}
      value={current}
      onFocus={loadEligible}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-[hsl(var(--border))] px-2 py-1 text-xs"
    >
      <option value="">— none —</option>
      {current && !(eligible || []).some((x) => x.id === current) && (
        <option value={current}>{current.slice(0, 8)}… (current)</option>
      )}
      {(eligible || []).map((x) => (
        <option key={x.id} value={x.id}>{x.full_name || x.email || x.public_staff_id}</option>
      ))}
    </select>
  );
}

/** Inline per-route daily shipment limit editor (blank = Unlimited). */
function LimitEditor({ route, onSave }) {
  const [val, setVal] = useState(route.daily_shipment_limit ?? "");
  useEffect(() => { setVal(route.daily_shipment_limit ?? ""); }, [route.daily_shipment_limit]);
  const dirty = String(val) !== String(route.daily_shipment_limit ?? "");
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min="1" step="1"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Unlimited"
        className="w-24 px-2 py-1 border border-[hsl(var(--border))] rounded text-sm"
        data-testid={`route-limit-input-${route.route_code}`}
      />
      <button
        className="btn-ghost text-xs disabled:opacity-40"
        disabled={!dirty}
        onClick={() => onSave(route.route_code, val)}
        data-testid={`route-limit-save-${route.route_code}`}
      >Save</button>
    </div>
  );
}

/** Missing endpoint-activation note for a route row (computed client-side from airport activation). */
function routeNote(route, airports) {
  if (route.effective_active) return null;
  const o = route.source_airport_code;
  const d = route.destination_airport_code;
  const oa = airports.find((a) => a.code === o)?.is_active_source;
  const da = airports.find((a) => a.code === d)?.is_active_destination;
  if (!route.active_for_booking) {
    return "Turn on the route flag to make it eligible (after endpoint airports are active).";
  }
  if (!oa && !da) return `Activate ${o} as an active origin and ${d} as an active destination.`;
  if (!oa) return `Activate ${o} as an active source airport.`;
  if (!da) return `Activate ${d} as an active destination airport.`;
  return null;
}

export default function AirportsRoutes() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);        // authoritative saved snapshot (drives ROUTES notes)
  const [draft, setDraft] = useState([]);              // staged AIRPORTS-section edits
  const [baseline, setBaseline] = useState("[]");      // JSON of last-saved draft for dirty compare
  const [saving, setSaving] = useState(false);
  const [navGuard, setNavGuard] = useState({ open: false, href: null });
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [belowUsageWarn, setBelowUsageWarn] = useState(null); // {routeCode, raw, message} | null
  const [selRoute, setSelRoute] = useState("");               // route_code of the one open route

  const load = async () => {
    setLoading(true);
    // Load airports, routes AND all holidays INDEPENDENTLY in parallel — a failure of one must
    // never blank the whole page, and holidays are read ONCE in bulk (no per-airport fan-out).
    const [a, r, h] = await Promise.allSettled([
      api.get("/admin/airports"),
      api.get("/admin/routes"),
      api.get("/admin/airports/holidays-bulk"),
    ]);
    let aps = [];
    if (a.status === "fulfilled") { aps = a.value.data; setAirports(aps); }
    else toast.error(a.reason?.displayMessage || "Failed to load airports");
    if (r.status === "fulfilled") setRoutes(r.value.data);
    else toast.error(r.reason?.displayMessage || "Failed to load routes");
    // Holidays are REQUIRED to seed the editable draft. A failed bulk read must surface as a load
    // failure — never be interpreted as "no holidays" (which would be a false empty configuration).
    if (h.status !== "fulfilled") {
      toast.error(h.reason?.displayMessage || "Failed to load airport holidays");
      setLoading(false);
      return;
    }
    const holByCode = h.value.data?.holidays_by_airport || {};
    // Seed the editable AIRPORTS draft (activation + working days + concierge + holidays).
    const d = aps.map((x) => ({
      code: x.code,
      city: x.city,
      airport_name: x.airport_name,
      is_active_source: !!x.is_active_source,
      is_active_destination: !!x.is_active_destination,
      working_days: maskToDays(x.working_weekdays),
      default_concierge_accounts_user_id: x.default_concierge_accounts_user_id || "",
      holidays: holByCode[x.code] || [],
    }));
    setDraft(d);
    setBaseline(JSON.stringify(d));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = JSON.stringify(draft) !== baseline;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  // Warn before leaving to another Admin tab with unsaved AIRPORTS edits (scoped: Admin nav only).
  useEffect(() => {
    const navEl = document.querySelector('[data-testid="admin-nav"]');
    if (!navEl) return undefined;
    const handler = (e) => {
      if (!dirtyRef.current) return;
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link || !navEl.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
      setNavGuard({ open: true, href: link.getAttribute("href") });
    };
    navEl.addEventListener("click", handler, true);
    return () => navEl.removeEventListener("click", handler, true);
  }, []);

  const updateDraft = (code, patch) =>
    setDraft((ds) => ds.map((x) => (x.code === code ? { ...x, ...patch } : x)));

  const saveSection = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.put("/admin/airports/section", {
        airports: draft.map((x) => ({
          code: x.code,
          is_active_source: x.is_active_source,
          is_active_destination: x.is_active_destination,
          working_days: x.working_days,                                 // null => 7-day
          default_concierge_accounts_user_id: x.default_concierge_accounts_user_id || null,
          holidays: x.holidays,
        })),
      });
      toast.success("Airports saved");
      await load();   // reconcile once with authoritative saved state; clears dirty
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || e?.displayMessage || "Save failed");
      // staged edits are intentionally preserved so the admin can retry/correct
    } finally {
      setSaving(false);
    }
  };

  const confirmLeave = () => {
    const href = navGuard.href;
    setDraft(JSON.parse(baseline));   // discard pending edits
    setNavGuard({ open: false, href: null });
    if (href) navigate(href);
  };

  if (profile && profile.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;

  const bookableLanes = routes.filter((r) => r.effective_active);

  const toggleRoute = async (routeCode, value) => {
    try {
      await api.patch(`/admin/routes/${routeCode}/activation`, { is_active: value });
      toast.success(`${routeCode} ${value ? "activated" : "deactivated"}`);
      await load();
    } catch (e) {
      toast.error(e.displayMessage || "Update failed");
    }
  };

  const saveLimit = async (routeCode, raw, acknowledge = false) => {
    const trimmed = String(raw).trim();
    let value;
    if (trimmed === "") {
      value = null; // Unlimited
    } else if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
      toast.error("Daily limit must be a positive whole number (or blank for Unlimited)");
      return;
    } else {
      value = Number(trimmed);
    }
    try {
      await api.patch(`/admin/routes/${routeCode}/capacity`, {
        daily_shipment_limit: value,
        acknowledge_below_usage: acknowledge,
      });
      toast.success(value === null ? `${routeCode} set to Unlimited` : `${routeCode} daily limit set to ${value}`);
      setBelowUsageWarn(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "CAPACITY_BELOW_TODAY_USAGE") {
        setBelowUsageWarn({ routeCode, raw, message: detail.message });
        return;
      }
      toast.error(e.displayMessage || "Update failed");
    }
  };

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[1200px] mx-auto p-6" data-testid="airports-routes-page">
      {belowUsageWarn && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="capacity-below-usage-modal">
          <div className="bg-[hsl(var(--card,0_0%_100%))] bg-white max-w-md w-full rounded-[4px] border border-[hsl(var(--border))] p-5 shadow-lg">
            <h3 className="heading text-lg mb-2">Limit below today’s bookings</h3>
            <p className="text-sm text-[hsl(var(--muted))] leading-relaxed" data-testid="capacity-below-usage-message">{belowUsageWarn.message}</p>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn-ghost" onClick={() => setBelowUsageWarn(null)} data-testid="capacity-below-usage-cancel">Cancel</button>
              <button className="btn-primary" onClick={() => saveLimit(belowUsageWarn.routeCode, belowUsageWarn.raw, true)} data-testid="capacity-below-usage-confirm">Save anyway</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="overline">CONFIGURATION</div>
          <h1 className="heading text-3xl">Airports &amp; Routes</h1>
        </div>
      </div>

      <div className="grid-card p-4 mb-6 border-l-2 border-[hsl(var(--accent))]" data-testid="activation-note">
        <p className="text-sm text-[hsl(var(--muted))]">
          Activation is explicit. An <strong>active airport</strong> does not make a{" "}
          <strong>route</strong> active. A route prices only when its flag is on{" "}
          <strong>and</strong> its source airport is an active origin{" "}
          <strong>and</strong> its destination airport is an active destination. Public users
          never pick airport codes — these settings drive address/pincode inference.
        </p>
      </div>

      <div className="mb-6">
        <div className="grid-card p-4" data-testid="bookable-lanes-summary">
          <div className="overline">CURRENTLY BOOKABLE LANES</div>
          <div className="heading text-2xl mt-1" data-testid="bookable-lanes-count">{bookableLanes.length}</div>
          {bookableLanes.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-2">
              {bookableLanes.map((r) => (
                <span key={r.route_code} className="id-chip text-xs" data-testid={`bookable-lane-${r.route_code}`}>
                  {r.route_label || `${r.source_airport_code} → ${r.destination_airport_code}`}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-[hsl(var(--muted))] mt-2">
              No effectively-active lanes. A lane needs its route flag on AND both origin/destination airports active.
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mono text-sm">Loading…</div>
      ) : (
        <>
          {/* Airports */}
          <section className="mb-8" data-testid="airports-section">
            <h2 className="overline mb-3">AIRPORTS</h2>
            <div className="grid-card overflow-hidden">
              <div className="table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-[hsl(var(--border))] text-[hsl(var(--muted))]">
                    <th className="p-3">Code</th>
                    <th className="p-3">City / Airport</th>
                    <th className="p-3">
                      <span className="inline-flex items-center gap-1"><AirplaneTakeoff size={14} /> Active origin</span>
                    </th>
                    <th className="p-3">
                      <span className="inline-flex items-center gap-1"><AirplaneLanding size={14} /> Active destination</span>
                    </th>
                    <th className="p-3">Pickup calendar</th>
                    <th className="p-3">Default Concierge Accounts</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.map((a) => (
                    <tr key={a.code} className="border-b border-[hsl(var(--border))]" data-testid={`airport-row-${a.code}`}>
                      <td className="p-3 mono font-semibold">{a.code}</td>
                      <td className="p-3">
                        <div>{a.city}</div>
                        <div className="text-xs text-[hsl(var(--muted))]">{a.airport_name}</div>
                      </td>
                      <td className="p-3">
                        <Switch
                          checked={!!a.is_active_source}
                          onCheckedChange={(v) => updateDraft(a.code, { is_active_source: v })}
                          data-testid={`airport-origin-toggle-${a.code}`}
                        />
                      </td>
                      <td className="p-3">
                        <Switch
                          checked={!!a.is_active_destination}
                          onCheckedChange={(v) => updateDraft(a.code, { is_active_destination: v })}
                          data-testid={`airport-destination-toggle-${a.code}`}
                        />
                      </td>
                      <td className="p-3 align-top">
                        <AirportPickupCell
                          airport={a}
                          days={a.working_days}
                          holidays={a.holidays}
                          onDaysChange={(days) => updateDraft(a.code, { working_days: days })}
                          onHolidaysChange={(h) => updateDraft(a.code, { holidays: h })}
                        />
                      </td>
                      <td className="p-3">
                        <ConciergeDefaultCell
                          airport={a}
                          value={a.default_concierge_accounts_user_id}
                          onChange={(v) => updateDraft(a.code, { default_concierge_accounts_user_id: v })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                className="btn-primary disabled:opacity-40"
                disabled={!dirty || saving}
                onClick={saveSection}
                data-testid="airports-save-btn"
              >
                {saving ? "Saving…" : "Save airports"}
              </button>
              {dirty && !saving && (
                <span className="text-xs text-[hsl(var(--muted))]" data-testid="airports-dirty-note">
                  You have unsaved changes
                </span>
              )}
            </div>
          </section>

          <AlertDialog open={navGuard.open} onOpenChange={(o) => !o && setNavGuard({ open: false, href: null })}>
            <AlertDialogContent data-testid="airports-unsaved-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes in the Airports section. Leaving this page will discard them.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="airports-unsaved-stay">Stay on this page</AlertDialogCancel>
                <AlertDialogAction onClick={confirmLeave} data-testid="airports-unsaved-leave">Leave &amp; discard</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Routes — one selector, one inline config panel (mirrors Pricing) */}
          <section data-testid="routes-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="overline"><span className="inline-flex items-center gap-1"><Path size={14} /> ROUTES</span></h2>
              <AddRouteDialog airports={airports} onCreated={load} />
            </div>
            <label className="block mb-4 max-w-sm">
              <div className="overline mb-1.5">Route</div>
              <select
                className="input-field"
                value={selRoute}
                onChange={(e) => setSelRoute(e.target.value)}
                data-testid="route-config-select"
              >
                <option value="">Select a route…</option>
                {routes.map((r) => (
                  <option key={r.route_code} value={r.route_code}>
                    {r.source_airport_code} → {r.destination_airport_code}
                    {r.active_for_booking ? "" : " · inactive"}
                  </option>
                ))}
              </select>
            </label>

            {(() => {
              const r = routes.find((x) => x.route_code === selRoute);
              if (!r) return null;
              const note = routeNote(r, airports);
              return (
                <div className="grid-card p-5" data-testid={`route-config-panel-${r.route_code}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="mono font-semibold text-lg">
                        {r.source_airport_code} → {r.destination_airport_code}
                      </div>
                      <div className="text-sm text-[hsl(var(--muted))]">{r.route_label || "—"}</div>
                    </div>
                    <span
                      className={`mono text-xs px-2 py-1 rounded ${
                        r.active_for_booking && r.effective_active
                          ? "bg-green-100 text-green-800"
                          : "bg-neutral-200 text-neutral-600"
                      }`}
                      data-testid={`route-effective-${r.route_code}`}
                    >
                      {r.active_for_booking ? (r.effective_active ? "ACTIVE" : "INACTIVE") : "INACTIVE"}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center gap-3" data-testid={`route-flag-row-${r.route_code}`}>
                    <Switch
                      checked={!!r.active_for_booking}
                      onCheckedChange={(v) => toggleRoute(r.route_code, v)}
                      data-testid={`route-toggle-${r.route_code}`}
                    />
                    <span className="text-sm">
                      Route flag {r.active_for_booking ? "on" : "off"}
                      {!r.active_for_booking && " — turn on to configure and make this route bookable"}
                    </span>
                  </div>

                  {note && (
                    <div className="text-xs text-amber-700 mt-2 max-w-[420px]" data-testid={`route-note-${r.route_code}`}>
                      {note}
                    </div>
                  )}

                  {r.active_for_booking && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`route-operational-${r.route_code}`}>
                      <div>
                        <div className="overline mb-1.5">Daily limit</div>
                        <div data-testid={`route-limit-cell-${r.route_code}`}>
                          <LimitEditor route={r} onSave={saveLimit} />
                        </div>
                      </div>
                      <div>
                        <div className="overline mb-1.5">Pickup hours</div>
                        <RoutePickupCell route={r} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <p className="text-xs text-[hsl(var(--muted))] mt-3">
              An active airport does not make a route active. A route is bookable only when its flag is
              on and both endpoint airports are active for their respective directions.
            </p>
          </section>
        </>
      )}
    </div>
    </>
  );
}
